/**
 * Builds a query string matching the imago-server CRUD middleware's expected
 * shape (Sequelize-native via Express's `qs` extended parser).
 *
 * Supports nested objects so callers can express
 *   where: { email: { $iLike: '%alice%' }, state: 'A' }
 *   order: [['email', 'ASC'], ['login', 'DESC']]
 */

export type QueryWhereLeaf = string | number | boolean | null | undefined;
export type QueryWhereObject = { [op: string]: QueryWhereValue };
export type QueryWhereValue =
    QueryWhereLeaf | QueryWhereLeaf[] | QueryWhereObject | QueryWhereObject[];

export interface PageQueryArgs {
    offset: number;
    limit: number;
    includeCount?: boolean;
    order?: Array<[field: string, dir: 'ASC' | 'DESC']>;
    where?: Record<string, QueryWhereValue>;
    /** Top-level query params appended outside of `where` (e.g. tenantId). */
    extraParams?: Record<string, string>;
}

const append = (params: URLSearchParams, key: string, value: QueryWhereLeaf): void => {
    if (value === null || value === undefined) return;
    params.append(key, String(value));
};

const isPlainObject = (v: unknown): v is Record<string, QueryWhereValue> =>
    v !== null && typeof v === 'object' && !Array.isArray(v);

export const serializeWhere = (
    params: URLSearchParams,
    where: Record<string, QueryWhereValue>,
    parentKey = 'where',
): void => {
    for (const [field, value] of Object.entries(where)) {
        if (value === null || value === undefined) continue;
        const key = `${parentKey}[${field}]`;
        if (Array.isArray(value)) {
            value.forEach((v, i) => {
                if (isPlainObject(v)) {
                    serializeWhere(params, v, `${key}[${i}]`);
                } else {
                    append(params, `${key}[${i}]`, v as QueryWhereLeaf);
                }
            });
        } else if (isPlainObject(value)) {
            serializeWhere(params, value, key);
        } else {
            append(params, key, value);
        }
    }
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Builds an OR-of-`$iLike` `where` fragment for text/varchar columns.
 * UUID columns cannot use $iLike (Postgres type mismatch); pass them as
 * `uuidColumns` and they'll be included as `$eq` only when `term` is a full
 * valid UUID. Shared by the paged list pages and the TopBar typeahead so a
 * search means the same thing everywhere.
 */
export const buildSearchWhere = (
    columns: readonly string[],
    term: string,
    uuidColumns?: readonly string[],
): { $or: QueryWhereObject[] } => {
    const pattern = `%${term}%`;
    const clauses: QueryWhereObject[] = columns.map((col) => ({ [col]: { $iLike: pattern } }));
    if (uuidColumns?.length && UUID_PATTERN.test(term)) {
        uuidColumns.forEach((col) => clauses.push({ [col]: { $eq: term } }));
    }
    return { $or: clauses };
};

export const buildPagedQueryString = (args: PageQueryArgs): string => {
    const params = new URLSearchParams();
    params.set('offset', String(args.offset));
    params.set('limit', String(args.limit));
    if (args.includeCount) params.set('includeCount', '1');
    if (args.order && args.order.length > 0) {
        args.order.forEach((entry, i) => {
            params.set(`order[${i}][0]`, entry[0]);
            params.set(`order[${i}][1]`, entry[1]);
        });
    }
    if (args.where) serializeWhere(params, args.where);
    if (args.extraParams) {
        for (const [key, value] of Object.entries(args.extraParams)) {
            params.set(key, value);
        }
    }
    return params.toString();
};
